import { useState, useEffect } from 'react';
import { SummaryCards } from '../../components/SummaryCards';
import { ActionRequired } from '../../components/ActionRequired';
import { ActiveProjects, type Project } from '../../components/ActiveProjects';
import { ContractsOverview, type ContractsData } from '../../components/ContractsOverview';
import { ProfileCompletion } from '../../components/ProfileCompletion';
import { RecentActivity, type Activity } from '../../components/RecentActivity';
import { RecentNotifications, type Notification } from '../../components/MessagesNotifications';
import { IncomingLeads, type Lead } from '../../components/IncomingLeads';
import { AcceptedLeads, type AcceptedLead } from '../../components/AcceptedLeadsD';
import { 
  SummaryCardsSkeleton,
  ActionRequiredSkeleton,
  ActiveProjectsSkeleton,
  ContractsOverviewSkeleton,
  ProfileCompletionSkeleton,
  RecentActivitySkeleton,
  MessagesSkeleton,
  IncomingLeadsSkeleton,
  AcceptedLeadsSkeleton
} from '../../components/shimmer';
import {
  ActionRequiredEmpty,
  ActiveProjectsEmpty,
  MessagesEmpty,
  RecentActivityEmpty,
  ContractsEmpty,
  IncomingLeadsEmpty,
  AcceptedLeadsEmpty
} from '../../components/EmptyStates';
import { CallAPIGetPromise } from 'Common/commaon';
import { useSelector } from 'react-redux';
import { selectUserDetails } from 'slices/userProfile/reducer';
import { Toaster } from 'components/ui/sonner';
import { Plus, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './contractor-dashboard.scss';

type LoadingState = 'loading' | 'loaded';

interface DashboardData {
  summaryCards: { live_projects: number; homeowner_sign_pending: number; contractor_sign_pending: number } | null;
  actionRequired: any[] | null;
  projects: Project[] | null;
  contracts: ContractsData | null;
  recentActivity: Activity[] | null;
  messages: Notification[] | null;
  incomingLeads: Lead[] | null;
  acceptedLeads: AcceptedLead[] | null;
  profileCompletion:any;

}

export default function App() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const user = useSelector(selectUserDetails)
  const currentUser = Array.isArray(user) ? user[0] : user;
  const contractorUserId = currentUser?.id;
  const contractorFirstName =
    currentUser?.user_first_name?.trim() ||
    currentUser?.first_name?.trim() ||
    currentUser?.firstName?.trim() ||
    currentUser?.name?.trim() ||
    'there';
  const [refresh,setRefresh]=useState(false)

  const [loadingState, setLoadingState] = useState<Record<string, LoadingState>>({
    summaryCards: 'loading',
    actionRequired: 'loading',
    projects: 'loading',
    contracts: 'loading',
    profile: 'loading',
    recentActivity: 'loading',
    messages: 'loading',
    incomingLeads: 'loading',
    acceptedLeads: 'loading'
  });

  const [data, setData] = useState<DashboardData>({
    summaryCards: null,
    actionRequired: null,
    projects: null,
    contracts: null,
    recentActivity: null,
    messages: null,
    incomingLeads: null,
    acceptedLeads: null,
    profileCompletion:{},
   
  });
  // Simulate progressive data loading with realistic mixed outcomes
  useEffect(() => {
 

    // Scenario 1: All caught up - no actions needed
    // Scenario 2: Busy contractor - mixed states with data
    // Scenario 3: New user - mostly empty
    // Scenario 4: Completely empty dashboard

    const loadData = async () => {
      CallAPIGetPromise("/dashboard/contractor").then((res:any)=>{
        setData(prev => ({
          ...prev,
          summaryCards: res.dashboard,
          actionRequired:res.actions,
          projects:res.projects,
          contracts:res.contracts,
          incomingLeads:res.leads,
          profileCompletion:res.profileCompletion,
          acceptedLeads:res.acceptedLeads,
          recentActivity:res.activities,messages:res.notifications
        }));
      setLoadingState(prev => ({ ...prev,acceptedLeads:"loaded"  ,incomingLeads:"loaded" ,messages:"loaded",recentActivity:"loaded",contracts:"loaded",profile:"loaded", summaryCards: 'loaded',actionRequired: 'loaded' ,projects:"loaded" }));
      }).finally(() => setIsRefreshing(false))
    };
if(contractorUserId){
  loadData();

}
  }, [contractorUserId, refresh]);
  const handleRefresh = () => {
    if (isRefreshing) return;

    setIsRefreshing(true);
    
    // Reset all loading states
    setLoadingState({
      summaryCards: 'loading',
      actionRequired: 'loading',
      projects: 'loading',
      contracts: 'loading',
      profile: 'loading',
      recentActivity: 'loading',
      messages: 'loading',
      incomingLeads: 'loading',
      acceptedLeads: 'loading'
    });

    // Reset all data
    setData({
      summaryCards: null,
      actionRequired: null,
      projects: null,
      contracts: null,
      recentActivity: null,
      messages: null,
      incomingLeads: null,
      acceptedLeads: null,
      profileCompletion:{},
   
    });
    setRefresh(r=>!r)
  };


  return (
    <div className="tailwind-prefix contractor-dashboard min-h-screen">
      
      <main>
        <section className="contractor-dashboard__hero">
          <div>
            <div className="contractor-dashboard__eyebrow">
              <span className="contractor-dashboard__status-dot" aria-hidden="true" />
              Contractor workspace
            </div>
            <h1>Welcome back, {contractorFirstName}</h1>
            <p>Track contracts, signatures, projects, and the work that needs your attention.</p>
          </div>
          <div className="contractor-dashboard__hero-actions">
            <button className="contractor-dashboard__primary-action" onClick={() => navigate('/create-contract')}>
              <Plus size={15} aria-hidden="true" />
              Create contract
            </button>
            <button className="contractor-dashboard__refresh" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw size={15} className={isRefreshing ? 'contractor-dashboard__spin' : ''} aria-hidden="true" />
              {isRefreshing ? 'Refreshing' : 'Refresh'}
            </button>
          </div>
        </section>

        {/* Summary Metrics */}
        <div className="contractor-dashboard__summary mb-12">
          {loadingState.summaryCards === 'loading' ? (
            <SummaryCardsSkeleton />
          ) : (
            <SummaryCards {...data.summaryCards!} />
          )}
        </div>

        {/* Hero Section - Action Required */}
        <div className="contractor-dashboard__section mb-14">
          {loadingState.actionRequired === 'loading' ? (
            <ActionRequiredSkeleton />
          ) : data.actionRequired && data.actionRequired.length === 0 ? (
            <ActionRequiredEmpty />
          ) : (
            <ActionRequired  afterAction={()=>{
            handleRefresh()
            }} actions={data.actionRequired ?? undefined} />
          )}
        </div>

        {/* Main Content - Active Projects */}
        <div className="contractor-dashboard__section mb-14">
          {loadingState.projects === 'loading' ? (
            <ActiveProjectsSkeleton />
          ) : data.projects && data.projects.length === 0 ? (
            <ActiveProjectsEmpty />
          ) : (
            <ActiveProjects  projects={data.projects ?? undefined} />
          )}
        </div>

        {/* Two Column Layout - Contracts and Profile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {/* Contracts Overview */}
          <div className="lg:col-span-2">
            {loadingState.contracts === 'loading' ? (
              <ContractsOverviewSkeleton />
            ) : data.contracts && 
               data.contracts.draft.length === 0 && 
               data.contracts.pending.length === 0 && 
               data.contracts.signed.length === 0 ? (
              <ContractsEmpty />
            ) : (
              <ContractsOverview contracts={data.contracts ?? undefined} />
            )}
          </div>

          {/* Profile Completion */}
          <div>
            {loadingState.profile === 'loading' ? (
              <ProfileCompletionSkeleton />
            ) : (
              <ProfileCompletion profileCompletion={data.profileCompletion} />
            )}
          </div>
        </div>

        {/* Secondary Information - Activity and Messages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-6">
          {loadingState.recentActivity === 'loading' ? (
            <RecentActivitySkeleton />
          ) : data.recentActivity && data.recentActivity.length === 0 ? (
            <RecentActivityEmpty />
          ) : (
            <RecentActivity activities={data.recentActivity ?? undefined} />
          )}

          {loadingState.messages === 'loading' ? (
            <MessagesSkeleton />
          ) : data.messages && data.messages.length === 0 ? (
            <MessagesEmpty />
          ) : (
            <RecentNotifications notifications={data.messages ?? undefined} />
          )}
        </div>

        {/* Leads Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {loadingState.incomingLeads === 'loading' ? (
            <IncomingLeadsSkeleton />
          ) : data.incomingLeads && data.incomingLeads.length === 0 ? (
            <IncomingLeadsEmpty />
          ) : (
            <IncomingLeads leads={data.incomingLeads ?? undefined} />
          )}

          {loadingState.acceptedLeads === 'loading' ? (
            <AcceptedLeadsSkeleton />
          ) : data.acceptedLeads && data.acceptedLeads.length === 0 ? (
            <AcceptedLeadsEmpty />
          ) : (
            <AcceptedLeads leads={data.acceptedLeads ?? undefined} />
          )}
        </div>
      </main>
      <Toaster position={"top-right"}/>
    </div>
  );
}
